import { m, type Message } from '@/i18n';
import type { Command } from '../types';
import type { PendingStimulus, Policy, QuestionClass } from './types';

/**
 * **The questions a rule can hit, and the policies that answer them**
 * (docs/RESOLUTION.md §3, §12 step 6). An automation never waits: when its act
 * raises a question, the rule's POLICY for that class answers it - fixed when
 * the rule was written, per class: `{ answer }` (the author pre-answered it),
 * `propose` (file the question with the item's owners, once per rule, item and
 * class), or `skip` (do nothing, and say so as a finding).
 *
 * A leaf of the resolver on purpose: it imports types only, so the flow
 * definition's validator can ask it without the resolver's steps (which read
 * the flow's act compiler) forming a cycle with the definition module.
 */

/** §6's three presets, as the answers a policy may name. */
export const LEAVE_ANSWER = 'leave';
export const SWEEP_ANSWER = 'sweep';
export const FORCE_ANSWER = 'force';

/** The out-of-role confirmation's two answers (§1 step 6). A policy may only
 *  say yes: "no" is what `skip` already means, with a finding. */
export const GO_AHEAD_ANSWER = 'yes';
export const LEAVE_IT_ANSWER = 'no';

/** The answer keys a policy may pre-name, per class. `cascade-size` is an
 *  absolute refusal (§6), so nothing answers it. */
export const POLICY_ANSWERS: Readonly<Record<QuestionClass, readonly string[]>> = {
  'open-children': [LEAVE_ANSWER, SWEEP_ANSWER, FORCE_ANSWER],
  'out-of-role': [GO_AHEAD_ANSWER],
  'cascade-size': [],
};

/** Whether a command concludes the item it is dispatched on. */
function concludes(command: Command): boolean {
  return command.type === 'Close';
}

/**
 * **Which questions this stimulus can raise when a machine sends it** (§3:
 * "derivable from the acts it can dispatch"). A conclusion can meet open real
 * children (§6); every act a flow dispatches can meet the out-of-role
 * confirmation, because a flow's machine actor is never a party to the item.
 * `cascade-size` is never here: it is a refusal, never asked. Pure.
 */
export function reachableQuestionClasses(pending: PendingStimulus): QuestionClass[] {
  const closes =
    pending.kind === 'cancel' ||
    (pending.kind === 'act' &&
      ('command' in pending.target
        ? concludes(pending.target.command)
        : pending.target.attribute === 'closed' && pending.target.value === 'yes'));
  return closes ? ['open-children', 'out-of-role'] : ['out-of-role'];
}

/**
 * Parse one authored policy for a class, or say why it is not one. Pure. An
 * answer must be one the class offers; `propose` and `skip` fit every class
 * that can be asked.
 */
export function parsePolicy(
  klass: QuestionClass,
  value: unknown,
): { ok: true; policy: Policy } | { ok: false; message: Message } {
  if (klass === 'cascade-size') {
    return {
      ok: false,
      message: m('flowErrors.policy.cascadeSize'),
    };
  }
  if (value === 'propose' || value === 'skip') return { ok: true, policy: value };
  if (typeof value === 'object' && value !== null && 'answer' in value) {
    const answer = (value as { answer: unknown }).answer;
    if (typeof answer === 'string' && POLICY_ANSWERS[klass].includes(answer)) {
      return { ok: true, policy: { answer } };
    }
    return {
      ok: false,
      message: m('flowErrors.policy.notAnAnswer', {
        answer: String(answer),
        question: klass,
        answers: POLICY_ANSWERS[klass].join(', '),
      }),
    };
  }
  return {
    ok: false,
    message: m('flowErrors.policy.shape', { question: klass }),
  };
}

/** Whether a string names a question class. */
export function isQuestionClass(value: string): value is QuestionClass {
  return value === 'open-children' || value === 'out-of-role' || value === 'cascade-size';
}

/** The words a finding and the editor use for a policy. */
export function describePolicy(policy: Policy | undefined): Message {
  if (policy === undefined || policy === 'skip') return m('flowErrors.policy.word.skip');
  if (policy === 'propose') return m('flowErrors.policy.word.propose');
  return m('flowErrors.policy.word.answer', { answer: policy.answer });
}
